import { grades } from '../../config/schools';
import { formatDateDisplay } from '../../utils/date';
import { isUuid } from './recordLinks';

export type RecordKind = 'student' | 'parent' | 'enrollment' | 'invoice';

export interface RecordHit {
  id: string;
  kind: RecordKind;
  name: string;
  /** Whatever tells two hits apart at a glance: school and grade, email, dates and amounts. */
  detail: string;
  /** A short tag for a row that is not live: "Inactive", "Voided". */
  badge: string | null;
}

/** Per kind. The palette is for jumping to something you can name, not for browsing. */
export const ADMIN_SEARCH_LIMIT = 5;

/**
 * The same rule as the database's `matches_keywords_distinct_fields_var`, which the
 * directory pages search with, so search and page agree on who matches: the whole phrase
 * inside any one field, or else each keyword inside a different field. "wang" finds a
 * Wang; "emily wang" needs Emily and Wang in two fields, not "wang" twice.
 *
 * Keywords and fields are both expected lowercased.
 */
export function matchesKeywords(keywords: string[], fields: string[]): boolean {
  if (keywords.length === 0) return true;
  const phrase = keywords.join(' ');
  if (fields.some(field => field.includes(phrase))) return true;
  const used = fields.map(() => false);
  return keywords.every(keyword => {
    const i = fields.findIndex((field, index) => !used[index] && field.includes(keyword));
    if (i < 0) return false;
    used[i] = true;
    return true;
  });
}

export const fullName = (first: string | null | undefined, last: string | null | undefined) =>
  [first, last].filter(Boolean).join(' ').trim() || 'Unnamed';

/** An embed comes back as an object, or as a one-element array when PostgREST can't tell. */
export function one<T>(embed: T | T[] | null | undefined): T | null {
  if (Array.isArray(embed)) return embed[0] ?? null;
  return embed ?? null;
}

interface NameEmbed { first_name: string | null; last_name: string | null }

/** Only the three `metadata` keys search reads, picked out server-side (the waiver stays put). */
export interface StudentIndexRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  active: boolean | null;
  school: string | null;
  grade: string | null;
  labels: string[] | null;
  student_parents: Array<{ parent: (NameEmbed & { email: string | null }) | Array<NameEmbed & { email: string | null }> | null }> | null;
}

export interface ParentIndexRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  active: boolean | null;
  student_parents: Array<{ student: NameEmbed | NameEmbed[] | null }> | null;
}

export interface EnrollmentIndexRow {
  id: string;
  student_id: string;
  is_active: boolean | null;
  enrollment_start_date: string | null;
  created_at: string;
  programs: { name: string | null; type: string | null } | Array<{ name: string | null; type: string | null }> | null;
}

interface IndexedRecord {
  hit: RecordHit;
  /** Lowercased, one entry per field the database search looks at. */
  fields: string[];
}

interface IndexedEnrollment {
  hit: RecordHit;
  studentId: string;
}

/** Already in result order: people by last then first name, enrollments live then newest. */
export interface SearchIndex {
  students: IndexedRecord[];
  parents: IndexedRecord[];
  enrollments: IndexedEnrollment[];
}

const lower = (value: string | null | undefined) => (value ?? '').toLowerCase();
const joinDistinct = (values: Array<string | null | undefined>) =>
  [...new Set(values.filter((v): v is string => Boolean(v)))].join(' ').toLowerCase();
const specified = (value: string | null | undefined) => (value && value !== 'Not specified' ? value : null);
const byName = (a: { last: string; first: string }, b: { last: string; first: string }) =>
  a.last.localeCompare(b.last) || a.first.localeCompare(b.first);

export function buildSearchIndex(
  studentRows: StudentIndexRow[],
  parentRows: ParentIndexRow[],
  enrollmentRows: EnrollmentIndexRow[],
): SearchIndex {
  const studentNames = new Map(studentRows.map(s => [s.id, fullName(s.first_name, s.last_name)]));

  // A child is found by the programs they are in now, as search_students does.
  const activePrograms = new Map<string, Array<{ name: string | null; type: string | null }>>();
  for (const e of enrollmentRows) {
    const program = one(e.programs);
    if (!e.is_active || !program) continue;
    activePrograms.set(e.student_id, [...(activePrograms.get(e.student_id) ?? []), program]);
  }

  const students = studentRows
    .map(s => {
      const parents = (s.student_parents ?? []).map(sp => one(sp.parent)).filter(Boolean) as Array<NameEmbed & { email: string | null }>;
      const programs = activePrograms.get(s.id) ?? [];
      const gradeValue = specified(s.grade);
      const grade = gradeValue ? grades.find(g => g.value === gradeValue)?.label ?? gradeValue : null;
      return {
        last: lower(s.last_name),
        first: lower(s.first_name),
        record: {
          hit: {
            id: s.id,
            kind: 'student' as const,
            name: fullName(s.first_name, s.last_name),
            detail: [specified(s.school), grade].filter(Boolean).join(' · '),
            badge: s.active === false ? 'Inactive' : null,
          },
          fields: [
            lower(s.first_name),
            lower(s.last_name),
            lower(s.school),
            lower(s.grade),
            joinDistinct(Array.isArray(s.labels) ? s.labels : []),
            joinDistinct(parents.map(p => p.first_name)),
            joinDistinct(parents.map(p => p.last_name)),
            joinDistinct(parents.map(p => p.email)),
            joinDistinct(programs.map(p => p.name)),
            joinDistinct(programs.map(p => p.type)),
          ],
        },
      };
    })
    .sort(byName)
    .map(s => s.record);

  const parents = parentRows
    .map(p => {
      const children = (p.student_parents ?? []).map(sp => one(sp.student)).filter(Boolean) as NameEmbed[];
      return {
        last: lower(p.last_name),
        first: lower(p.first_name),
        record: {
          hit: {
            id: p.id,
            kind: 'parent' as const,
            name: fullName(p.first_name, p.last_name),
            detail: p.email ?? '',
            badge: p.active === false ? 'Inactive' : null,
          },
          fields: [
            lower(p.first_name),
            lower(p.last_name),
            lower(p.email),
            lower(p.phone),
            joinDistinct(children.map(c => c.first_name)),
            joinDistinct(children.map(c => c.last_name)),
          ],
        },
      };
    })
    .sort(byName)
    .map(p => p.record);

  // Live ones first — the class a parent is asking about is almost always the current one.
  const enrollments = [...enrollmentRows]
    .sort((a, b) => Number(b.is_active !== false) - Number(a.is_active !== false) || b.created_at.localeCompare(a.created_at))
    .map(e => ({
      studentId: e.student_id,
      hit: {
        id: e.id,
        kind: 'enrollment' as const,
        name: `${studentNames.get(e.student_id) ?? 'Unknown student'} · ${one(e.programs)?.name ?? 'Unknown program'}`,
        detail: e.enrollment_start_date ? `From ${formatDateDisplay(e.enrollment_start_date)}` : '',
        badge: e.is_active === false ? 'Inactive' : null,
      },
    }));

  return { students, parents, enrollments };
}

export interface LocalSearchResult {
  students: RecordHit[];
  parents: RecordHit[];
  enrollments: RecordHit[];
}

/**
 * Students and parents matching the term, and the enrollments of those students — there is
 * no keyword search over enrollments, so a name finds the child and their classes come with
 * them. A pasted id is looked up as each kind; ids are uuids, so at most one kind hits.
 */
export function searchLocalIndex(index: SearchIndex, term: string): LocalSearchResult {
  const trimmed = term.trim().toLowerCase();
  if (isUuid(trimmed)) {
    return {
      students: index.students.filter(s => s.hit.id === trimmed).map(s => s.hit),
      parents: index.parents.filter(p => p.hit.id === trimmed).map(p => p.hit),
      enrollments: index.enrollments.filter(e => e.hit.id === trimmed).map(e => e.hit),
    };
  }
  const keywords = trimmed.split(/\s+/).filter(Boolean);
  const top = (records: IndexedRecord[]) =>
    records.filter(r => matchesKeywords(keywords, r.fields)).slice(0, ADMIN_SEARCH_LIMIT).map(r => r.hit);
  const students = top(index.students);
  const studentIds = new Set(students.map(s => s.id));
  return {
    students,
    parents: top(index.parents),
    enrollments: index.enrollments
      .filter(e => studentIds.has(e.studentId))
      .slice(0, ADMIN_SEARCH_LIMIT)
      .map(e => e.hit),
  };
}
